/* ============================================================================
 * Modal de detalhe de documento fiscal (NFC-e / NFS-e).
 *
 * Consome GET /fiscal/invoices/:id (FiscalDocumentDetailDto —
 * fiscal-documents.dto.ts:128). Aberto ao clicar numa linha das listagens.
 * ========================================================================== */

import { useAuth } from '../../../shared/auth/AuthContext';
import { formatCurrency, formatDateTime, EMPTY } from '../../../shared/utils/format';
import { Badge, Button, LoadingSkeleton, Modal } from '@nexora/design-system';
import { useFiscalDocumentDetail } from './fiscalDocumentsHooks';
import {
  documentStatusLabel,
  documentStatusVariant,
  documentStatusIcon,
} from './fiscalStatusMaps';

interface FiscalDocumentDetailModalProps {
  documentId: string | null;
  onClose: () => void;
}

export function FiscalDocumentDetailModal({
  documentId,
  onClose,
}: FiscalDocumentDetailModalProps): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { data: doc, isLoading, error } = useFiscalDocumentDetail(tenantId, documentId);

  const docTypeLabel = (type: string | null | undefined): string => {
    switch (type) {
      case 'NFCE':
        return 'NFC-e';
      case 'NFSE':
        return 'NFS-e';
      case 'NFE':
        return 'NF-e';
      default:
        return type || '—';
    }
  };

  return (
    <Modal
      isOpen={!!documentId}
      onClose={onClose}
      title={doc ? `${docTypeLabel(doc.type)} ${doc.documentNumber ?? ''}`.trim() : 'Documento fiscal'}
      size="lg"
      footer={
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {isLoading && <LoadingSkeleton count={8} height="24px" />}

      {error && !isLoading && (
        <div className="fiscal-state">
          <p className="fiscal-state__title">Erro ao carregar o documento</p>
          <p className="fiscal-state__detail">{(error as Error).message}</p>
        </div>
      )}

      {doc && !isLoading && !error && (
        <>
          <div className="fiscal-detail-grid">
            <div className="fiscal-detail-item">
              <div className="label">Status</div>
              <div className="value">
                <Badge variant={documentStatusVariant(doc.status)} size="sm">
                  <span className="material-symbols-outlined fiscal-status-icon" aria-hidden="true">
                    {documentStatusIcon(doc.status)}
                  </span>
                  {documentStatusLabel(doc.status)}
                </Badge>
              </div>
            </div>
            <div className="fiscal-detail-item">
              <div className="label">Número / Série</div>
              <div className="value value--mono">
                {doc.documentNumber ? `${doc.documentNumber} / ${doc.serie ?? 1}` : EMPTY}
              </div>
            </div>
            <div className="fiscal-detail-item">
              <div className="label">Emissão</div>
              <div className="value">{formatDateTime(doc.issuedAt)}</div>
            </div>
            <div className="fiscal-detail-item">
              <div className="label">Autorização</div>
              <div className="value">{formatDateTime(doc.authorizationAt)}</div>
            </div>
            {doc.terminalPdv && (
              <div className="fiscal-detail-item">
                <div className="label">Terminal PDV</div>
                <div className="value">{doc.terminalPdv}</div>
              </div>
            )}
            {doc.competenceDate && (
              <div className="fiscal-detail-item">
                <div className="label">Competência</div>
                <div className="value">{formatDateTime(doc.competenceDate)}</div>
              </div>
            )}
            {doc.customerName && (
              <div className="fiscal-detail-item">
                <div className="label">Cliente</div>
                <div className="value">{doc.customerName}</div>
              </div>
            )}
            {doc.serviceDescription && (
              <div className="fiscal-detail-item span-2">
                <div className="label">Serviço prestado</div>
                <div className="value">{doc.serviceDescription}</div>
              </div>
            )}
            <div className="fiscal-detail-item span-2">
              <div className="label">Chave de acesso</div>
              <div className="value value--mono">{doc.accessKey ?? EMPTY}</div>
            </div>
            {doc.cfop && (
              <div className="fiscal-detail-item">
                <div className="label">CFOP</div>
                <div className="value value--mono">{doc.cfop}</div>
              </div>
            )}
            {doc.nfeProtocol && (
              <div className="fiscal-detail-item">
                <div className="label">Protocolo</div>
                <div className="value value--mono">{doc.nfeProtocol}</div>
              </div>
            )}
            <div className="fiscal-detail-item">
              <div className="label">Valor total</div>
              <div className="value value--mono">{formatCurrency(doc.totalValue)}</div>
            </div>
            {doc.rejectionMessage && (
              <div className="fiscal-detail-item span-2">
                <div className="label">Motivo da rejeição</div>
                <div className="value">{doc.rejectionMessage}</div>
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}